"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Mic, Radio, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const employees = [{
  name: "Marcus Sterling",
  position: "Chief Strategy Officer",
  bio: "A veteran architect of market dominance, Marcus specializes in high-stakes brand positioning and competitive intelligence."
}, {
  name: "Elena Vance",
  position: "Director of Media Planning",
  bio: "Elena leverages algorithmic precision to optimize media spend, ensuring every dollar drives measurable growth."
}];
export default function BehindTheMic() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: cardsRef,
    isVisible: isCardsVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="221572" id="the-minds-behind-the-strategy" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-40 min-h-screen flex items-center">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661389/img/architectural_cityscape_monochrome.png)] bg-cover bg-center opacity-20 blur-sm scale-105" />
        {}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background" />
        
        {}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-[120px] mix-blend-screen" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-chart-3/10 rounded-full blur-[120px] mix-blend-screen" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div className="col-span-1 lg:col-span-7 flex flex-col justify-center pt-12 lg:pt-24">
            <div ref={headerRef} className={`relative transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              {}
              <span className="absolute -top-16 -left-8 md:-top-24 md:-left-12 text-8xl md:text-[12rem] font-default font-black text-muted/10 select-none tracking-tighter pointer-events-none">
                ELITE
              </span>
              
              {}
              <div className="relative z-20 mb-8">
                <h2 className="text-5xl md:text-7xl lg:text-8xl font-default font-black uppercase tracking-tighter leading-[0.85] text-transparent bg-clip-text bg-gradient-to-br from-foreground to-foreground/60">
                  The Minds
                </h2>
                <h2 className="text-5xl md:text-7xl lg:text-8xl font-default font-black uppercase tracking-tighter leading-[0.85] text-primary absolute top-6 md:top-10 left-4 md:left-8 z-[-1] opacity-40 mix-blend-screen blur-[2px]">
                  Behind
                </h2>
              </div>
            </div>

            <div ref={textRef} className={`relative z-20 max-w-2xl mt-12 transition-all duration-1000 delay-300 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-[1px] w-12 bg-primary" />
                <span className="font-mono text-primary uppercase tracking-widest text-sm font-semibold">
                  Mission Statement
                </span>
              </div>
              <p className="font-default text-xl md:text-2xl text-muted-foreground leading-relaxed mb-8">
                At <strong className="text-foreground font-default font-bold">Benchmark Media</strong>, we engineer market dominance through architectural precision. Our consultancy blends data-driven intelligence with elite creative execution to outperform competitors in dynamic markets.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="font-default uppercase tracking-wider font-bold rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(45,91,255,0.3)] transition-all duration-300">
                  <Mic className="mr-2 size-5" />
                  Our Solutions
                </Button>
                <Button size="lg" variant="outline" className="font-default uppercase tracking-wider font-bold rounded-sm border-primary/30 text-foreground hover:bg-primary/5 transition-all duration-300">
                  <Radio className="mr-2 size-5" />
                  The Method
                </Button>
              </div>
            </div>
          </div>

          {}
          <div ref={cardsRef} className="col-span-1 lg:col-span-4 lg:col-start-9 relative mt-12 lg:-mt-12">
            <div className="space-y-8">
              {employees.map((employee, i) => <div key={i} data-index={i} className={`transition-all duration-1000 ease-out ${isCardsVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`} style={{
              transitionDelay: `${(i + 1) * 200}ms`
            }}>
                  <Card className={`bg-white/5 backdrop-blur-xl border-white/10 shadow-2xl hover:border-primary/40 transition-all duration-500 rounded-sm ${i % 2 !== 0 ? 'lg:-ml-12' : 'lg:ml-12'}`}>
                    <CardHeader className="pb-2 relative overflow-hidden">
                      {}
                      <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/5 rounded-full blur-2xl" />
                      
                      <div className="flex items-center justify-between relative z-10">
                        <CardTitle className="font-default text-2xl tracking-tight text-foreground font-bold">
                          {employee.name}
                        </CardTitle>
                        <Sparkles className="size-5 text-primary opacity-50" />
                      </div>
                      <CardDescription className="font-mono text-primary font-medium uppercase tracking-wider text-xs">
                        {employee.position}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="relative z-10">
                      <p className="font-default text-muted-foreground text-sm leading-relaxed">
                        {employee.bio}
                      </p>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
            
            {}
            <div className="hidden lg:block absolute top-0 -left-6 w-[1px] h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
          </div>

        </div>
      </div>
    </section>;
}